import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, MessageSquare, ArrowLeft, RefreshCw, Users } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { isUserAdmin } from '../../lib/auth';
import { LoadingSpinner } from '../../components/LoadingSpinner';

interface ChatMessage {
  id: string;
  name: string;
  message: string;
  created_at: string;
}

interface Viewer {
  id: string;
  name: string;
  joined_at: string;
}

type Tab = 'messages' | 'attendees';

export function LiveChatAdmin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('messages');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [viewers, setViewers] = useState<Viewer[]>([]);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    checkAndLoad();
  }, [user]);

  const checkAndLoad = async () => {
    if (!user?.id) return;
    const admin = await isUserAdmin(user.id);
    if (!admin) { navigate('/login'); return; }
    await Promise.all([loadMessages(), loadViewers()]);
    subscribeRealtime();
    setLoading(false);
  };

  const loadMessages = async () => {
    const { data } = await supabase
      .from('live_chat_messages')
      .select('*')
      .order('created_at', { ascending: true });
    if (data) setMessages(data);
  };

  const loadViewers = async () => {
    const { data } = await supabase
      .from('live_viewers')
      .select('*')
      .order('joined_at', { ascending: false });
    if (data) setViewers(data);
  };

  const subscribeRealtime = () => {
    supabase
      .channel('live_chat_admin')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'live_chat_messages' }, payload => {
        setMessages(prev => [...prev, payload.new as ChatMessage]);
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'live_chat_messages' }, payload => {
        setMessages(prev => prev.filter(m => m.id !== (payload.old as ChatMessage).id));
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'live_viewers' }, payload => {
        setViewers(prev => [payload.new as Viewer, ...prev]);
      })
      .subscribe();
  };

  useEffect(() => {
    if (tab === 'messages') bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, tab]);

  const deleteMessage = async (id: string) => {
    await supabase.from('live_chat_messages').delete().eq('id', id);
  };

  const clearAllMessages = async () => {
    if (!confirm('Delete all messages? This cannot be undone.')) return;
    setClearing(true);
    await supabase.from('live_chat_messages').delete().neq('id', '00000000-0000-0000-0000-000000000000');
    setMessages([]);
    setClearing(false);
  };

  const clearAllViewers = async () => {
    if (!confirm('Clear the attendee list? This cannot be undone.')) return;
    await supabase.from('live_viewers').delete().neq('id', '00000000-0000-0000-0000-000000000000');
    setViewers([]);
  };

  const formatTime = (iso: string) =>
    new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });

  if (loading) return <LoadingSpinner message="Loading live chat…" />;

  return (
    <div className="min-h-screen bg-brand-5" style={{ fontFamily: 'Urbanist, sans-serif' }}>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-20">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="p-2 hover:bg-gray-10 rounded-xl transition-colors">
              <ArrowLeft className="w-5 h-5 text-gray-50" />
            </button>
            <div className="p-2.5 bg-brand-90 rounded-xl shadow-soft">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-heading-xs text-gray-80">Live Chat</h1>
              <p className="text-text-sm text-gray-40">Admin only — real-time updates</p>
            </div>
          </div>
          <button
            onClick={() => Promise.all([loadMessages(), loadViewers()])}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-60 hover:bg-gray-10 rounded-xl transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Stats bar */}
        <div className="freud-card-elevated p-4 mb-5 flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold" style={{ background: 'rgba(231,76,60,0.08)', color: '#e74c3c' }}>
              <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: '#e74c3c', animation: 'livePulse 1.5s ease-in-out infinite' }} />
              Live
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-gray-60">
            <MessageSquare className="w-4 h-4 text-gray-40" />
            <span className="font-semibold text-gray-80">{messages.length}</span> message{messages.length !== 1 ? 's' : ''}
          </div>
          <div className="flex items-center gap-1.5 text-sm text-gray-60">
            <Users className="w-4 h-4 text-gray-40" />
            <span className="font-semibold text-gray-80">{viewers.length}</span> attendee{viewers.length !== 1 ? 's' : ''}
          </div>
          <p className="text-xs text-gray-40 ml-auto">Updates in real-time</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 rounded-xl bg-gray-10 mb-5 w-fit">
          <button
            onClick={() => setTab('messages')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${tab === 'messages' ? 'bg-white text-gray-80 shadow-soft' : 'text-gray-50 hover:text-gray-70'}`}
          >
            Messages
            {messages.length > 0 && (
              <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full font-bold" style={{ background: 'rgba(127,151,72,0.15)', color: '#4D5C2F' }}>
                {messages.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab('attendees')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${tab === 'attendees' ? 'bg-white text-gray-80 shadow-soft' : 'text-gray-50 hover:text-gray-70'}`}
          >
            Attendees
            {viewers.length > 0 && (
              <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full font-bold" style={{ background: 'rgba(127,151,72,0.15)', color: '#4D5C2F' }}>
                {viewers.length}
              </span>
            )}
          </button>
        </div>

        {/* ── Messages tab ───────────────────────────────────────── */}
        {tab === 'messages' && (
          <>
            <div className="flex justify-end mb-3">
              <button
                onClick={clearAllMessages}
                disabled={clearing || messages.length === 0}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-error-50 hover:bg-error-5 rounded-xl transition-colors disabled:opacity-40"
              >
                <Trash2 className="w-4 h-4" />
                Clear All
              </button>
            </div>

            {messages.length === 0 ? (
              <div className="freud-card-elevated p-16 text-center">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(127,151,72,0.1)' }}>
                  <MessageSquare className="w-6 h-6" style={{ color: '#7F9748' }} />
                </div>
                <p className="text-base font-semibold text-gray-70 mb-1">No messages yet</p>
                <p className="text-sm text-gray-40">Audience messages will appear here in real-time.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((msg, i) => {
                  const showDate = i === 0 || formatDate(messages[i - 1].created_at) !== formatDate(msg.created_at);
                  return (
                    <div key={msg.id}>
                      {showDate && (
                        <div className="flex items-center gap-3 my-4">
                          <div className="flex-1 h-px bg-gray-10" />
                          <span className="text-xs text-gray-40 font-medium">{formatDate(msg.created_at)}</span>
                          <div className="flex-1 h-px bg-gray-10" />
                        </div>
                      )}
                      <div className="freud-card-elevated p-4 flex items-start gap-4 group">
                        <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold text-white" style={{ background: '#7F9748' }}>
                          {msg.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 mb-1">
                            <span className="text-sm font-semibold text-gray-80">{msg.name}</span>
                            <span className="text-xs text-gray-40">{formatTime(msg.created_at)}</span>
                          </div>
                          <p className="text-sm text-gray-70 leading-relaxed break-words">{msg.message}</p>
                        </div>
                        <button
                          onClick={() => deleteMessage(msg.id)}
                          className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-error-5 rounded-lg transition-all flex-shrink-0"
                          title="Delete message"
                        >
                          <Trash2 className="w-4 h-4 text-error-50" />
                        </button>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
            )}
          </>
        )}

        {/* ── Attendees tab ──────────────────────────────────────── */}
        {tab === 'attendees' && (
          <>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-gray-50">{viewers.length} people have joined the stream</p>
              <button
                onClick={clearAllViewers}
                disabled={viewers.length === 0}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-error-50 hover:bg-error-5 rounded-xl transition-colors disabled:opacity-40"
              >
                <Trash2 className="w-4 h-4" />
                Clear List
              </button>
            </div>

            {viewers.length === 0 ? (
              <div className="freud-card-elevated p-16 text-center">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(127,151,72,0.1)' }}>
                  <Users className="w-6 h-6" style={{ color: '#7F9748' }} />
                </div>
                <p className="text-base font-semibold text-gray-70 mb-1">No attendees yet</p>
                <p className="text-sm text-gray-40">Names will appear here as people join the stream.</p>
              </div>
            ) : (
              <div className="freud-card-elevated overflow-hidden">
                {viewers.map((viewer, i) => (
                  <div
                    key={viewer.id}
                    className={`flex items-center gap-4 px-5 py-3.5 ${i < viewers.length - 1 ? 'border-b border-gray-10' : ''}`}
                  >
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold text-white" style={{ background: '#7F9748' }}>
                      {viewer.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="flex-1 text-sm font-semibold text-gray-80">{viewer.name}</span>
                    <span className="text-xs text-gray-40">Joined {formatTime(viewer.joined_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      <style>{`@keyframes livePulse { 0%,100%{opacity:1} 50%{opacity:0.25} }`}</style>
    </div>
  );
}
